// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL;
console.log('[AuthContext - Environment Check]', {
  apiUrl: process.env.REACT_APP_API_URL,
  nodeEnv: process.env.NODE_ENV
});

// Role to permissions mapping
const ROLE_PERMISSIONS = {
  super_admin: [
    'manage_admins', 'manage_users', 'manage_waitlist', 'reset_passwords',
    'view_reports', 'manage_content', 'delete_content',
    'view_analytics', 'view_user_metrics', 'view_content_metrics', 
    'view_technical_metrics', 'manage_settings'
  ],
  user_manager: ['manage_users', 'manage_waitlist', 'reset_passwords'],
  content_moderator: ['view_reports', 'manage_content', 'delete_content'],
  analytics_viewer: ['view_analytics', 'view_user_metrics', 'view_content_metrics']
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  console.log('[AuthContext - State Update]', { user, loading, error });

  const logout = useCallback(async () => {
    try {
      console.log('[AuthContext - Logout]');
      
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        // Attempt to logout on server
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (err) {
      console.error('[AuthContext - Logout Error]', err);
    } finally {
      // Clean up local storage and state regardless of server response
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  }, []);

  const refreshAccessToken = useCallback(async () => {
    try {
      console.log('[AuthContext - Token Refresh]');
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) throw new Error('Token refresh failed');

      const { accessToken, user: userData } = await response.json();
      localStorage.setItem('accessToken', accessToken);
      setUser(userData);
      
      console.log('[AuthContext - Token Refresh Success]');
      
      return accessToken;
    } catch (err) {
      console.error('[AuthContext - Token Refresh Error]', err);
      logout();
      throw err;
    }
  }, [logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        
        console.log('[AuthContext - Init]', { hasAccessToken: !!accessToken });

        if (accessToken) {
          // Verify the session with backend
          const response = await fetch(`${API_URL}/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            console.log('[AuthContext - Session Verified]', { user: data.user });
          } else {
            // If verification fails, try refresh
            await refreshAccessToken();
          }
        }
      } catch (err) {
        console.error('[AuthContext - Init Error]', err);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [refreshAccessToken, logout]);

  const login = async (email, password) => {
    try {
      console.log('[AuthContext - Login Attempt]', { 
        email,
        apiUrl: process.env.REACT_APP_API_URL // Log the API URL
      });
      
      const url = `${process.env.REACT_APP_API_URL}/auth/login`;
      console.log('[AuthContext - Request URL]', url);
  
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
  
      console.log('[AuthContext - Response]', { 
        status: response.status,
        ok: response.ok 
      });
  
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }
  
      const { accessToken, refreshToken, user: userData } = await response.json();
      
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      
      setUser(userData);
      setError(null);
      
      console.log('[AuthContext - Login Success]', { userId: userData.id });
      
      return userData;
    } catch (err) {
      console.error('[AuthContext - Login Error]', {
        message: err.message,
        stack: err.stack,
        type: err.name
      });
      setError(err.message);
      throw err;
    }
  };

  const hasPermission = (requiredPermission) => {
    if (!user) return false;
    
    // Get default permissions for the user's role
    const rolePermissions = ROLE_PERMISSIONS[user.role] || [];
    
    // Combine role permissions with any additional custom permissions
    const allPermissions = [...new Set([...rolePermissions, ...(user.permissions || [])])];
    
    return allPermissions.includes(requiredPermission);
  };

  const hasAnyPermission = (requiredPermissions) => {
    return requiredPermissions.some(permission => hasPermission(permission));
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      login,
      logout,
      hasPermission,
      hasAnyPermission,
      refreshAccessToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};